import React, { useState, useEffect } from 'react';
import { ExternalLink, Calendar, MapPin } from 'lucide-react';
import { safeUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// ── Featured hero card (full-width or half-width) ───────────────────────────────

const FeaturedCard = ({ event, half = false }) => (
  <div
    className={`grid overflow-hidden border border-gray-800 ${half ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}
    style={{ background: '#0a0a0a' }}
  >
    {/* Poster */}
    <div className="relative bg-gray-900" style={{ minHeight: half ? 260 : 400 }}>
      {event.poster_url && safeUrl(event.poster_url) ? (
        <img
          src={safeUrl(event.poster_url)}
          alt={event.name}
          className="w-full h-full object-cover object-top"
          style={{ minHeight: half ? 260 : 400 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{ minHeight: half ? 260 : 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111' }}>
          <Calendar size={32} stroke="#222" />
        </div>
      )}
    </div>

    {/* Details */}
    <div className={`flex flex-col justify-between ${half ? 'p-7' : 'p-10'}`} style={{ background: '#0a0a0a' }}>
      <div>
        <h3 className={`text-white mb-1 ${half ? 'heading-small' : 'heading-medium'}`}>{event.name}</h3>

        <div className="flex flex-col gap-2 mb-5 mt-3">
          {event.dates && (
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest" style={{ color: '#FFD700' }}>
              <Calendar size={12} />
              <span>{event.dates}</span>
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-gray-400">
              <MapPin size={12} />
              <span>{event.location}</span>
            </div>
          )}
        </div>

        {event.description && (
          <p className="body-small text-gray-400 leading-relaxed mb-6" style={{ fontSize: half ? '0.8rem' : undefined }}>
            {event.description}
          </p>
        )}

        {event.open_to_everyone && (
          <div className="border border-gray-800 p-4 mb-6">
            <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mb-1">Open to Everyone</p>
            <p className="text-sm text-gray-400">
              {event.open_to_everyone_note || 'This event highlights Black creators and supporters while welcoming all who celebrate and support minority communities.'}
            </p>
          </div>
        )}
      </div>

      {event.ticket_url && safeUrl(event.ticket_url) && (
        <a
          href={safeUrl(event.ticket_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium uppercase tracking-wide transition-colors duration-200"
          style={{ background: '#FFD700', color: '#000' }}
          onMouseEnter={e => e.currentTarget.style.background = '#e6c200'}
          onMouseLeave={e => e.currentTarget.style.background = '#FFD700'}
        >
          <span>Get Tickets</span>
          <ExternalLink size={13} />
        </a>
      )}
    </div>
  </div>
);

// ── Upcoming event card (grid) ──────────────────────────────────────────────────

const UpcomingCard = ({ event }) => (
  <div className="flex flex-col overflow-hidden border border-gray-800" style={{ background: '#0a0a0a' }}>
    {/* Poster thumbnail */}
    <div style={{ aspectRatio: '16/9', background: '#111', overflow: 'hidden', flexShrink: 0 }}>
      {event.poster_url && safeUrl(event.poster_url) ? (
        <img
          src={safeUrl(event.poster_url)}
          alt={event.name}
          className="w-full h-full object-cover object-top"
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Calendar size={24} stroke="#222" />
        </div>
      )}
    </div>

    {/* Details */}
    <div className="flex flex-col flex-1 p-5">
      <h4 className="text-white font-semibold mb-3 leading-snug" style={{ fontSize: '1rem' }}>{event.name}</h4>

      <div className="flex flex-col gap-1.5 mb-4">
        {event.dates && (
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest" style={{ color: '#FFD700' }}>
            <Calendar size={11} /><span>{event.dates}</span>
          </div>
        )}
        {event.location && (
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500">
            <MapPin size={11} /><span>{event.location}</span>
          </div>
        )}
      </div>

      {(event.short_description || event.description) && (
        <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {event.short_description || event.description}
        </p>
      )}

      {event.ticket_url && safeUrl(event.ticket_url) && (
        <a
          href={safeUrl(event.ticket_url)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 py-3 text-xs font-medium uppercase tracking-wide transition-colors duration-200 mt-auto"
          style={{ background: '#FFD700', color: '#000' }}
          onMouseEnter={e => e.currentTarget.style.background = '#e6c200'}
          onMouseLeave={e => e.currentTarget.style.background = '#FFD700'}
        >
          <span>Get Tickets</span>
          <ExternalLink size={11} />
        </a>
      )}
    </div>
  </div>
);

// ── Section ─────────────────────────────────────────────────────────────────────

const FeaturedEventSection = () => {
  const [featured, setFeatured] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/events`)
      .then(r => r.json())
      .then(data => {
        setFeatured(data.featured || []);
        setUpcoming(data.upcoming || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!featured.length && !upcoming.length) return null;

  return (
    <section className="section-padding bg-black" id="events">
      <div className="max-w-7xl mx-auto">

        {/* ── Featured ── */}
        {featured.length > 0 && (
          <>
            <div className="mb-8">
              <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mb-2">BGN Events</p>
              <h2 className="heading-large lowercase" style={{ color: '#FFD700' }}>
                {featured.length === 1 ? 'Featured Event' : 'Featured Events'}
              </h2>
            </div>

            {featured.length === 1 ? (
              <FeaturedCard event={featured[0]} />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {featured.map(e => <FeaturedCard key={e.id} event={e} half />)}
              </div>
            )}
          </>
        )}

        {/* ── Upcoming ── */}
        {upcoming.length > 0 && (
          <div className={featured.length > 0 ? 'mt-16' : ''}>
            {featured.length === 0 && (
              <div className="mb-8">
                <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mb-2">BGN Events</p>
                <h2 className="heading-large lowercase" style={{ color: '#FFD700' }}>Upcoming Events</h2>
              </div>
            )}
            {featured.length > 0 && (
              <div className="mb-8">
                <p className="text-xs font-medium uppercase tracking-widest text-gray-500 mb-1">Also Coming Up</p>
                <h3 className="heading-small text-white lowercase">More Events</h3>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map(e => <UpcomingCard key={e.id} event={e} />)}
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default FeaturedEventSection;
